/* eslint-disable max-len */
import WarningTwoToneIcon from '@mui/icons-material/WarningTwoTone';
import { Box, Button, CircularProgress, Link, Pagination, Table, TableBody, TableCell, TableRow, Typography, useMediaQuery } from "@mui/material";
import React from "react";
import { ShortageFilter } from "../../components/DOAForecastComponents/ShortageFilter";
import { MainContainer } from "../../components/MainContainerComp";
import { PlateModal } from '../../components/PlateModal';
import ScrollIndicatorsComp from '../../components/ScrollIndicatorsComp/ScrollIndicatorsComp';
import { theme } from '../../utils/theme';
import { IDispatchAvailabilityListFilter, IDispatchAvailabilityListReportResultsData } from "./DispatchAvailabilityListReportResultasData";
import { RowsPerPage } from './DispatchAvailabilityListReportResultsModels';
import { PaginationStyle, RangeTextStyle, RangeWrapperStyle, boardDescriptionStyle, boardPlateStyle, filterButtonStyle, jobDetailsButtonStyle, subheaderStyle, phoneStyle, refreshButtonStyle, refreshInfoStyle, regularWorkForceStyle, shiftPecentageText, squamishPlateStyle, tableStyle, warningIconStyle } from "./styled";

interface IDispatchAvailabilityListReportResultsProps {
  data?: IDispatchAvailabilityListReportResultsData;
  filter: IDispatchAvailabilityListFilter,
  onFilterUpdate: (updatedFilter: IDispatchAvailabilityListFilter) => void;
  onRefresh: () => void;
  loading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (_event: React.ChangeEvent<unknown>, page: number) => void;
  onSetOpenJobDetails: (boardCode: string, ci: number) => void;
  jobDetailsInfo: { boardCode: string, candidateInfoIndex: number }[];
  onOpenClosePlateModal: (regId?: number, board?: string) => void,
  board: string,
  regId: number,
  date: string,
  shift: string
}

const renderPhone = (phoneNumber: string | null, label: string) => (
  <Typography sx={phoneStyle}>
    <Typography component={'span'}>
      {label}: {phoneNumber ? <Link href={`tel:${phoneNumber}`}>{phoneNumber}</Link> : '--'}
    </Typography>
  </Typography>
);

const getButtonStyle = (ci: any) => {
  if (ci.isInSquamishComplement && !ci.isRwf) {
    return squamishPlateStyle;
  } else if (!ci.isInSquamishComplement && ci.isRwf) {
    return regularWorkForceStyle;
  } else {
    return boardPlateStyle;
  }
}

const renderButton = (ci: any, onOpenClosePlateModal: any) => {
  const buttonStyle = getButtonStyle(ci);
  return (
    <Button
      sx={buttonStyle}
      onClick={() => onOpenClosePlateModal(ci.registrationNumber, ci.boardCode)}
    >
      {ci.boardCode}{ci.plate}
    </Button>
  );
}

export const DispatchAvailabilityListReportResults: React.FC<IDispatchAvailabilityListReportResultsProps> = ({
  data,
  filter,
  onFilterUpdate,
  onRefresh,
  loading,
  page,
  totalPages,
  onPageChange,
  onSetOpenJobDetails,
  jobDetailsInfo,
  onOpenClosePlateModal,
  board,
  regId,
  date,
  shift
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Calculate the range of candidates being displayed
  const startIndex = (page - 1) * RowsPerPage + 1;
  const endIndex = Math.min(startIndex + (RowsPerPage - 1), data?.candidateInfo.length || 0);
  return (
    <MainContainer hasFullSreenTabletSize={true}>
      <Box>
        <Box sx={subheaderStyle}>
          <Typography sx={{ pr: '100px' }} className='sub-title'>
            {`Possible Candidates for: ${date}, ${shift} Shift for ${data?.jobDescription} job`}
          </Typography>
          <ShortageFilter
            filter={filter}
            boardOptions={data?.boardOptions || []}
            onFilterUpdate={onFilterUpdate}
            checkboxesList={[
              { label: 'Used the DOA and not dispatched', claim: 'usedDOAnotDispatched' },
              { label: 'No DOA and not dispatched', claim: 'noDOAnotDispatched' },
              { label: 'Has job on current shift', claim: 'hasJobOnCurrentShift' },
              { label: 'Has job on previous shift', claim: 'hasJobOnPreviousShift' }
            ]}
          />
        </Box>
      </Box>
      <Typography
        component={'div'}
        variant='container'
        data-automation="data-container"
      >
        <ScrollIndicatorsComp>
          <Box sx={{ position: 'relative', display: 'inline-block', minWidth: '100%' }}>
            {loading && <CircularProgress style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />}
            <Table>
              <TableBody>
                {!data?.boardData.length && <TableRow><TableCell sx={{ textAlign: 'center' }} colSpan={5}>No Data</TableCell></TableRow>}
                {
                  data && data.boardData.map((bd, i) => {
                    return (
                      <TableRow key={i}>
                        <TableCell colSpan={5} sx={boardDescriptionStyle}>
                          <Typography component='span' sx={{ fontWeight: 'bold' }}>{bd.boardDescription}</Typography>
                          {
                            bd.buttonInfo.map((bi, i) => (
                              <React.Fragment key={i}>
                                {bi.buttonImage && <img src={`/assets/images/buttons/${bi.buttonImage}`} alt='button' />}
                                {bi.buttonDescription && <Typography component='span'>{bi.buttonDescription} (Plate: {bi.plateNumber})</Typography>}
                              </React.Fragment>
                            ))
                          }
                          <Table sx={tableStyle} size="custom">
                            <TableBody>
                              {
                                bd.candidateInfo?.map((ci, i) => (
                                  <React.Fragment key={ci.registrationNumber}>
                                    <TableRow className='mainRow'>
                                      <TableCell sx={{ background: ci.dispatchStatusColor, width: '30%' }}>
                                        {ci.dispatchStatus}
                                      </TableCell>
                                      <TableCell sx={{ background: ci.insertColor ?? 'inherit', width: '30%' }}>
                                        <Typography sx={{ pr: '30px', boxSizing: 'border-box', position: 'relative' }}>
                                          {ci.employeeFullName}
                                          {
                                            ci.hasJob && <Button
                                              sx={jobDetailsButtonStyle}
                                              onClick={() => onSetOpenJobDetails(bd.boardCode, i)}
                                            >
                                              {ci.jobDetailsButton}
                                            </Button>
                                          }
                                        </Typography>
                                        {ci.lastOrderDate && <Typography>Last Worked: {ci.lastOrderDate}</Typography>}
                                        <Typography>{ci.lastDespatchShiftDescription}</Typography>
                                        {ci.epdSuspensionLevelDescription && <Typography sx={{ textAlign: 'left' }}>
                                          <WarningTwoToneIcon sx={warningIconStyle} />
                                          {ci.epdSuspensionLevelDescription}
                                        </Typography>}
                                        {ci.isRwf && <Typography>RWF</Typography>}
                                        {ci.isInSquamishComplement && <Typography>SQUAMISH COMPLEMENT</Typography>}
                                      </TableCell>
                                      <TableCell>
                                        <Typography component={'span'} sx={shiftPecentageText}>
                                          <Typography sx={{ textAlign: 'left' }}>G-Yard:</Typography>
                                          <Typography sx={{ textAlign: 'right' }}>{ci.graveyardShiftPercentage}%</Typography>
                                        </Typography>
                                        <Typography component={'span'} sx={shiftPecentageText}>
                                          <Typography sx={{ textAlign: 'left' }}>Day:</Typography>
                                          <Typography sx={{ textAlign: 'right' }}>{ci.dayShiftPercentage}%</Typography>
                                        </Typography>
                                        <Typography component={'span'} sx={shiftPecentageText}>
                                          <Typography sx={{ textAlign: 'left' }}>Night:</Typography>
                                          <Typography sx={{ textAlign: 'right' }}>{ci.nightShiftPercentage}%</Typography>
                                        </Typography>
                                      </TableCell>
                                      <TableCell sx={{ textAlign: 'center' }}>
                                        <Typography>{ci.registrationNumber}</Typography>
                                        {renderButton(ci, onOpenClosePlateModal)}
                                      </TableCell>
                                      <TableCell>
                                        {renderPhone(ci?.phonePrimary, 'P')}
                                        {renderPhone(ci?.phoneAlternate, 'A')}
                                        {renderPhone(ci?.phoneCell, 'C')}
                                      </TableCell>
                                    </TableRow>
                                    {jobDetailsInfo.find(jdi => jdi.boardCode === bd.boardCode && jdi.candidateInfoIndex === i) &&
                                      <TableRow sx={{ background: 'white' }}>
                                        <TableCell colSpan={5}>
                                          <Typography>{ci.jobDetailsButton === 'CB' ? 'Callback' : 'Job'} Details:</Typography>
                                          <Typography>{ci.jobDetails}</Typography>
                                        </TableCell>
                                      </TableRow>}
                                  </React.Fragment>
                                ))
                              }
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    )
                  })
                }
              </TableBody>
            </Table>
          </Box>
          <Box sx={RangeWrapperStyle}>
            {!!data?.candidateInfo.length && <Typography sx={RangeTextStyle}>
              {`Displaying ${startIndex} to ${endIndex} of ${data?.candidateInfo.length} candidates.`}
            </Typography>}
            {
              totalPages > 1 && <Pagination
                sx={PaginationStyle}
                onChange={onPageChange}
                color="primary"
                page={page}
                count={totalPages}
                variant="outlined"
                shape="rounded"
                size={isMobile ? 'small' : 'medium'}
              />
            }
          </Box>
          {board && regId && <PlateModal
            onClose={() => onOpenClosePlateModal()}
            open={!!regId && !!board}
            board={board}
            id={Number(regId)}
          />}
          <Typography sx={refreshInfoStyle}>
            {data?.refreshTime}
            <Button
              onClick={() => onRefresh()}
              variant="contained"
              size="medium"
              data-automation="refresh-button"
              sx={refreshButtonStyle}
            >
              Refresh
            </Button>
          </Typography>
        </ScrollIndicatorsComp>
      </Typography>
    </MainContainer>
  )
}